<script lang="ts" setup>
import type { ColorPair } from '~/models/Presentation/BasePage'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useRouter }  from '#imports'
import { computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useFullscreen } from '@vueuse/core'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import usePendo from '~/composables/usePendo'
import { useContentHelper } from '~/composables/useContentHelper'
import FullscreenButton from '~/components/utils/FullscreenButton.vue'
import CloseButton from '~/components/utils/CloseButton.vue'
import Button from '~/components/ui/Button.vue'
import FavoriteDialog from '~/components/favorites/dialogs/FavoriteDialog.vue'

const {
  variant = 'default',
  item,
  colorPair,
  favorite = true,
  fullscreen = true,
} = defineProps<{
  variant?: 'default' | 'colorPair'
  item?: BaseItem
  colorPair?: ColorPair
  favorite?: boolean
  fullscreen?: boolean
}>()

const { viewsWithTrackingEnabled } = usePendo()
const { isAuthenticated, isTeacher } = storeToRefs(useAuthStore())
const { isFullscreen } = useFullscreen()
const { isColophon } = useContentHelper()
const router = useRouter()

const borderColor = computed(() => variant === 'colorPair' ? colorPair?.text : 'var(--color-gray-50)')
const textHoverColor = computed(() => variant === 'colorPair' ? colorPair?.background : 'var(--color-white)')

const isFavoriteNavigation = computed(() => !!router.currentRoute.value.params?.listId)
const isTaskNavigation = computed(() => !!router.currentRoute.value.params?.taskId)
const isColophonView = computed(() => item && isColophon(item))

const showFavoriteButton = computed(() => favorite
  && !isFavoriteNavigation.value
  && !isTaskNavigation.value
  && !isColophonView.value
  && isTeacher.value)

let fullscreenPendo = ''
let closePendo = ''

onMounted(() => {
  if (item?.contentTypeIdentifier && viewsWithTrackingEnabled.includes(item?.contentTypeIdentifier)) {
    fullscreenPendo = PendoFeature.Presentation.Fullscreen
    closePendo = PendoFeature.Presentation.Exit
  }
})
</script>

<template>
  <div class="pointer-events-none fixed inset-x-0 top-0 z-10 grid grid-cols-2 p-4 *:pointer-events-auto print:hidden">
    <div class="flex max-w-fit items-center gap-2">
      <FavoriteDialog
        v-if="isAuthenticated && !isFullscreen && showFavoriteButton && item"
        class="top-button-theme"
        action="addTo"
        :item
      >
        <template #activator="{ icon, onActivate }">
          <Button
            shape="square"
            size="xl"
            variant="outline"
            @click="onActivate"
          >
            <font-awesome-icon :icon style="--fa-secondary-opacity: 0" />
          </Button>
        </template>
      </FavoriteDialog>
      <slot />
    </div>

    <div class="top-button-theme flex items-center gap-2 place-self-end">
      <FullscreenButton
        v-if="isAuthenticated && fullscreen"
        :data-pendo="fullscreenPendo"
      />
      <CloseButton
        v-if="isAuthenticated"
        :data-pendo="closePendo"
        :item
      />
    </div>
  </div>
</template>

<style scoped>
.top-button-theme {
  &:deep(button),
  &:deep(a) {
    background: v-bind(textHoverColor);
    outline-color: v-bind(borderColor);
    color: v-bind(borderColor);

    &:hover {
      color: v-bind(textHoverColor);
      background: v-bind(borderColor);
    }
  }
}
</style>
